import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import SurveyPainChart from './SurveyPainChart';
import { fetchSurveyResponses } from '../utils/fetchSurveyResponses';
import {
  LANG_LABELS,
  SURVEY_CSV_HEADERS,
  computeSurveyStats,
  filterSurveyRows,
  formatAnswersLines,
  getUniqueSurveyors,
  relativeTime,
  resolveSurveyAnswerDisplay,
  surveyCsvFilename,
  surveyRowsToCsvData,
} from '../utils/surveyResponseHelpers';
import { buildCsvFromRows, downloadCsvContent } from '../utils/csvExport';
import { formatSurveyorLabel, listSurveyors, syncSurveyorsFromServer } from '../utils/surveySurveyorRegistry';

const GOLD = '#C9A84C';
const NAVY = '#14213d';
const PAGE_SIZE = 20;

const selectClass = 'rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function phoneDigits(num) {
  return String(num || '').replace(/\D/g, '').slice(-10);
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      className="rounded-2xl border p-4 sm:p-5"
      style={{
        background: `linear-gradient(145deg, ${NAVY}, #0a1628)`,
        borderColor: highlight ? GOLD : `${GOLD}40`,
        borderWidth: highlight ? 2 : 1,
      }}
    >
      <p className="m-0 text-2xl font-extrabold text-white sm:text-3xl">{value}</p>
      <p className="mt-1 text-xs font-medium uppercase tracking-wider text-[#8896a8]">{label}</p>
    </div>
  );
}

function HotLeadCard({ row, t }) {
  const phone = phoneDigits(row.whatsapp_number);
  const waHref = phone ? `https://wa.me/91${phone}` : null;
  const telHref = phone ? `tel:+91${phone}` : null;

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: `${GOLD}50`, background: 'rgba(201,168,76,0.06)' }}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="m-0 text-base font-bold text-white">
            {String.fromCodePoint(0x1F525)}
            {' '}
            {row.respondent_name || '—'}
          </p>
          <p className="m-0 mt-1 text-sm text-[#8896a8]">{row.village || '—'}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge className="border-de-gold/40 bg-de-gold/10 text-de-gold">
            {row.survey_type === 'client' ? t('surveyClient') : t('surveyOwner')}
          </Badge>
          <Badge className="border-white/20 bg-white/5 text-[#c8d0dc]">
            {LANG_LABELS[row.language] || row.language || '—'}
          </Badge>
        </div>
      </div>
      <p className="m-0 mt-2 text-sm text-[#c8d0dc]">
        {waHref ? (
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="text-de-gold underline">
            {row.whatsapp_number}
          </a>
        ) : (row.whatsapp_number || '—')}
      </p>
      <p className="m-0 mt-1 text-xs text-[#8896a8]">
        {relativeTime(row.submitted_at, t)}
        {' · '}
        source: {row.source || '—'}
        {row.surveyor ? ` · ${formatSurveyorLabel(row.surveyor)}` : ''}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {telHref && (
          <a href={telHref}>
            <Button type="button" size="sm" variant="outline" className="border-de-gold/40 text-de-gold">
              {t('surveyCall')}
            </Button>
          </a>
        )}
        {waHref && (
          <a href={waHref} target="_blank" rel="noopener noreferrer">
            <Button type="button" size="sm" className="bg-de-gold text-[#14213d] hover:bg-de-gold/90">
              WhatsApp
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

function PaymentStatusBadge({ row, t }) {
  const status = row.is_legacy ? 'legacy' : (row.payment_status || 'pending');
  const map = {
    legacy: 'bg-slate-800/80 text-slate-300 border-slate-500/30',
    pending: 'bg-amber-950/50 text-amber-200 border-amber-400/30',
    approved: 'bg-emerald-950/50 text-emerald-300 border-emerald-400/30',
    rejected: 'bg-red-950/50 text-red-300 border-red-400/30',
  };
  const label = {
    legacy: t('surveyPaymentLegacy'),
    pending: t('surveyReviewStatusPending'),
    approved: t('surveyReviewStatusApproved'),
    rejected: t('surveyReviewStatusRejected'),
  };
  return (
    <div className="flex flex-col gap-1">
      <Badge className={map[status] || 'bg-white/10 text-white'}>
        {label[status] || status}
      </Badge>
      {row.payout_locked && (
        <span className="text-[10px] text-slate-400">{t('surveyReviewLocked')}</span>
      )}
    </div>
  );
}

export default function SurveyResponsesTab() {
  const { t } = useLanguage();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    surveyType: 'all',
    hotLeadOnly: false,
    language: 'all',
    surveyor: 'all',
    paymentStatus: 'all',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await syncSurveyorsFromServer();
    } catch (syncErr) {
      // Non-critical — continue loading
      console.warn('Surveyors sync failed:', syncErr);
    }
    try {
      const data = await fetchSurveyResponses();
      setAllRows(data);
    } catch (err) {
      const isAuthErr = err?.message === 'auth_required'
        || err?.message?.includes('auth');
      setError(isAuthErr
        ? 'Session expired. Please login again.'
        : (err?.message || t('surveyLoadFailed')));
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => computeSurveyStats(allRows), [allRows]);
  const surveyors = useMemo(() => {
    const codes = new Set([
      ...getUniqueSurveyors(allRows),
      ...listSurveyors().map((f) => f.code),
    ]);
    return [...codes].sort();
  }, [allRows]);
  const filtered = useMemo(() => filterSurveyRows(allRows, filters), [allRows, filters]);
  const hotLeads = useMemo(
    () => filtered.filter((r) => r.hot_lead).sort(
      (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at),
    ),
    [filtered],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const handleExport = async () => {
    const csv = buildCsvFromRows(SURVEY_CSV_HEADERS, surveyRowsToCsvData(filtered));
    await downloadCsvContent(csv, surveyCsvFilename());
  };

  if (loading) {
    return <p className="py-12 text-center text-de-muted">{t('surveyLoading')}</p>;
  }

  if (error) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-de-accent/40 bg-de-accent/10 p-4 text-de-accent">
          {error}
        </div>
        <Button type="button" variant="outline" className="border-de-gold/40 text-de-gold" onClick={load}>
          {t('surveyRetry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard label={t('surveyStatTotal')} value={stats.total} />
        <StatCard label={t('surveyStatHotLeads')} value={stats.hotLeads} highlight />
        <StatCard label={t('surveyStatToday')} value={stats.today} />
        <StatCard label={t('surveyStatTopSource')} value={stats.topSource} />
      </div>

      <SurveyPainChart rows={allRows} />

      <section>
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-de-gold">
          {t('surveyHotLeadsSection')}
        </h3>
        {hotLeads.length === 0 ? (
          <p className="text-sm text-de-muted">{t('surveyNoHotLeads')}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {hotLeads.map((row) => (
              <HotLeadCard key={row.id} row={row} t={t} />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-de-gold/20 bg-[#0c1a30]/60 p-4">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyFilterType')}
            <select
              className={selectClass}
              value={filters.surveyType}
              onChange={(e) => setFilters((f) => ({ ...f, surveyType: e.target.value }))}
            >
              <option value="all">{t('surveyAll')}</option>
              <option value="owner">{t('surveyOwner')}</option>
              <option value="client">{t('surveyClient')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyFilterHotLead')}
            <select
              className={selectClass}
              value={filters.hotLeadOnly ? 'hot' : 'all'}
              onChange={(e) => setFilters((f) => ({ ...f, hotLeadOnly: e.target.value === 'hot' }))}
            >
              <option value="all">{t('surveyAll')}</option>
              <option value="hot">{t('surveyHotOnly')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyFilterLanguage')}
            <select
              className={selectClass}
              value={filters.language}
              onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))}
            >
              <option value="all">{t('surveyAll')}</option>
              <option value="mr">मराठी</option>
              <option value="hi">हिंदी</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyFilterSurveyor')}
            <select
              className={selectClass}
              value={filters.surveyor}
              onChange={(e) => setFilters((f) => ({ ...f, surveyor: e.target.value }))}
            >
              <option value="all">{t('surveyAll')}</option>
              {surveyors.map((s) => (
                <option key={s} value={s}>{formatSurveyorLabel(s)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-de-muted">
            {t('surveyFilterPayment')}
            <select
              className={selectClass}
              value={filters.paymentStatus}
              onChange={(e) => setFilters((f) => ({ ...f, paymentStatus: e.target.value }))}
            >
              <option value="all">{t('surveyAll')}</option>
              <option value="legacy">{t('surveyPaymentLegacy')}</option>
              <option value="pending">{t('surveyReviewStatusPending')}</option>
              <option value="approved">{t('surveyReviewStatusApproved')}</option>
              <option value="rejected">{t('surveyReviewStatusRejected')}</option>
            </select>
          </label>
          <Button
            type="button"
            variant="outline"
            className="ml-auto border-de-gold/40 text-de-gold"
            onClick={handleExport}
            disabled={filtered.length === 0}
          >
            {t('surveyCsvExport')}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-de-gold/15 hover:bg-transparent">
                <TableHead className="text-de-gold">{t('surveyColDate')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColName')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColPhone')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColVillage')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColType')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColLang')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColSource')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColSurveyor')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyReviewColPayment')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColHot')}</TableHead>
                <TableHead className="text-de-gold">{t('surveyColTrial')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row) => {
                const expanded = expandedId === row.id;
                const trial = resolveSurveyAnswerDisplay(
                  row.survey_type,
                  row.language || 'mr',
                  'trial_interest',
                  row.answers?.trial_interest,
                );
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      className="cursor-pointer border-de-gold/10 hover:bg-de-gold/5"
                      onClick={() => setExpandedId(expanded ? null : row.id)}
                    >
                      <TableCell className="whitespace-nowrap text-xs">{formatDate(row.submitted_at)}</TableCell>
                      <TableCell>{row.respondent_name || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.whatsapp_number || '—'}</TableCell>
                      <TableCell>{row.village || '—'}</TableCell>
                      <TableCell>{row.survey_type || '—'}</TableCell>
                      <TableCell>{LANG_LABELS[row.language] || row.language || '—'}</TableCell>
                      <TableCell>{row.source || '—'}</TableCell>
                      <TableCell>{row.surveyor ? formatSurveyorLabel(row.surveyor) : '—'}</TableCell>
                      <TableCell><PaymentStatusBadge row={row} t={t} /></TableCell>
                      <TableCell>{row.hot_lead ? String.fromCodePoint(0x1F525) : '—'}</TableCell>
                      <TableCell className="text-xs">{trial}</TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="border-de-gold/10 bg-black/20">
                        <TableCell colSpan={11}>
                          <pre className="m-0 overflow-x-auto whitespace-pre-wrap font-mono text-xs text-[#c8d0dc]">
                            {formatAnswersLines(row.answers, {
                              surveyType: row.survey_type,
                              lang: row.language || 'mr',
                            }).join('\n') || '—'}
                          </pre>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-de-muted">{t('surveyNoMatch')}</p>
        )}

        {filtered.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-xs text-de-muted">
              {t('surveyShowing')
                .replace('{from}', String(page * PAGE_SIZE + 1))
                .replace('{to}', String(Math.min((page + 1) * PAGE_SIZE, filtered.length)))
                .replace('{total}', String(filtered.length))}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-de-gold/30"
                disabled={page <= 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('surveyPrev')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-de-gold/30"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('surveyNextPage')}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
